import type {
  CalendarEventGroupAnchorMode,
  PeopleTravelEvent,
  PeopleTravelLeg,
  PeopleTravelLocation,
  PeopleTravelOption,
  PeopleTravelStop,
  PeopleTravelTrip,
} from '../types';
import { LEG_MODE_LABELS } from './peopleTravelCanvasGraph';
import type { EventGroupSegmentPayload } from './executiveCalendarDelegatedService';

const DEFAULT_SEGMENT_MINUTES = 60;

function isoOrDefault(iso: string | undefined, fallbackDate: string, hour: number): string {
  if (iso) return iso;
  return `${fallbackDate}T${String(hour).padStart(2, '0')}:00:00`;
}

export function buildCalendarSegmentsFromTrip(args: {
  trip: PeopleTravelTrip;
  stops: PeopleTravelStop[];
  selectedOptions: PeopleTravelOption[];
  timeZone: string;
  primaryCalendarId: string;
  locations?: PeopleTravelLocation[];
  events?: PeopleTravelEvent[];
  legs?: PeopleTravelLeg[];
}): EventGroupSegmentPayload[] {
  const {
    trip,
    stops,
    selectedOptions,
    primaryCalendarId,
    locations = [],
    events = [],
    legs = [],
  } = args;
  const segments: EventGroupSegmentPayload[] = [];
  let segIndex = 0;
  const locById = new Map(locations.map(l => [l.id, l]));

  if (events.length || legs.length) {
    for (const event of events) {
      const loc = locById.get(event.locationId);
      const start = event.startAt || isoOrDefault(undefined, loc?.startDate || trip.startDate, 9 + segIndex);
      const end =
        event.endAt ||
        new Date(new Date(start).getTime() + DEFAULT_SEGMENT_MINUTES * 60_000).toISOString();
      segments.push({
        segmentId: `pt-event-${event.id}`,
        type: 'activity',
        calendarId: primaryCalendarId,
        title: `${trip.title}: ${event.title}`,
        location: loc?.label || event.title,
        durationMinutes: DEFAULT_SEGMENT_MINUTES,
        start,
        end,
      });
      segIndex += 1;
    }
    for (const leg of legs) {
      const from = locById.get(leg.fromLocationId);
      const to = locById.get(leg.toLocationId);
      const start = isoOrDefault(undefined, from?.startDate || trip.startDate, 8 + segIndex);
      const end = new Date(new Date(start).getTime() + DEFAULT_SEGMENT_MINUTES * 60_000).toISOString();
      segments.push({
        segmentId: `pt-leg-${leg.id}`,
        type: 'activity',
        calendarId: primaryCalendarId,
        title: `${trip.title}: ${LEG_MODE_LABELS[leg.mode]} ${from?.label || '?'} → ${to?.label || '?'}`,
        location: [from?.label, to?.label].filter(Boolean).join(' → '),
        durationMinutes: DEFAULT_SEGMENT_MINUTES,
        start,
        end,
      });
      segIndex += 1;
    }
    if (segments.length) return segments;
  }

  const sortedStops = [...stops].sort((a, b) => a.sortOrder - b.sortOrder);

  for (const stop of sortedStops) {
    const start = stop.startAt || isoOrDefault(undefined, trip.startDate, 9 + segIndex);
    const end =
      stop.endAt ||
      new Date(new Date(start).getTime() + DEFAULT_SEGMENT_MINUTES * 60_000).toISOString();

    segments.push({
      segmentId: `pt-${stop.id}`,
      type: 'activity',
      calendarId: primaryCalendarId,
      title: `${trip.title}: ${stop.label}`,
      location: stop.address || stop.label,
      durationMinutes: DEFAULT_SEGMENT_MINUTES,
      start,
      end,
    });
    segIndex += 1;
  }

  for (const opt of selectedOptions) {
    const d = opt.details || {};
    if (opt.kind === 'flight') {
      const depAt = typeof d.departureAt === 'string' ? d.departureAt : undefined;
      const arrAt = typeof d.arrivalAt === 'string' ? d.arrivalAt : undefined;
      if (depAt && arrAt) {
        segments.push({
          segmentId: `pt-flight-${opt.id}`,
          type: 'activity',
          calendarId: primaryCalendarId,
          title: `${trip.title}: Flight ${opt.vendor || opt.label}`,
          location:
            [d.departureAirport, d.arrivalAirport].filter(Boolean).join(' → ') || opt.label,
          durationMinutes: Math.max(
            30,
            Math.round((new Date(arrAt).getTime() - new Date(depAt).getTime()) / 60_000),
          ),
          start: depAt,
          end: arrAt,
        });
      }
    }
  }

  if (segments.length === 0 && trip.destinations.length > 0) {
    const start = `${trip.startDate}T09:00:00`;
    const end = `${trip.endDate}T17:00:00`;
    segments.push({
      segmentId: `pt-trip-${trip.id}`,
      type: 'activity',
      calendarId: primaryCalendarId,
      title: trip.title,
      location: trip.destinations.map(d => d.label).join(' → '),
      durationMinutes: DEFAULT_SEGMENT_MINUTES,
      start,
      end,
    });
  }

  return segments;
}

export type TripCalendarSyncParams = {
  executiveUid: string;
  invitees: string[];
  anchorMode?: CalendarEventGroupAnchorMode;
  timeZone?: string;
  segments: EventGroupSegmentPayload[];
};
